// apps/client/src/pages/admin/dashboard/admin/components/FilterAdminDialog.tsx

import { useDialog } from "@/client/stores/dialog";
import { userFilterAdminStore } from "@/client/stores/filter-admin";
import { FilterUserType } from "@reactive-resume/dto";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { SelectFormField } from "../_components/select-form-fields";
import { useRoles } from "@/client/services/role/role";

type StringFilterCondition = "startsWith" | "contains" | "equals" | "endsWith";
type NumberFilterCondition = "gt" | "gte" | "equals" | "lt" | "lte";
type DateFilterCondition = "gt" | "gte" | "equals" | "lt" | "lte";
type BooleanFilterCondition = "equals";

export type AdminFormType = {
  name?: {
    condition: StringFilterCondition;
    value: string;
  };
  email?: {
    condition: StringFilterCondition;
    value: string;
  };
  roleId?: {
    condition: StringFilterCondition;
    value: string;
  };
  emailVerified?: {
    condition: BooleanFilterCondition;
    value: string;
  };
  isActivated?: {
    condition: BooleanFilterCondition;
    value: string;
  };
  createdAt?: {
    condition: DateFilterCondition;
    value: string;
  };
};

const ObjectConversionToFilterType = (object: AdminFormType) => {
  const filter: FilterUserType = {};
  Object.entries(object).forEach(([key, field]) => {
    if (!field || !field.value) return;

    filter[key as keyof FilterUserType] = {
      [field.condition]: field.value,
    };
  });
  return filter;
};

export const FilterAdminDialog = () => {
  type FormValue = AdminFormType;

  const formRef = useRef<HTMLFormElement>(null);
  const { replaceFilterAdmin, resetFilterAdmin } = userFilterAdminStore();
  const { isOpen, close } = useDialog("filter-admin");
  const { data: roles } = useRoles();

  const form = useForm<FormValue>({
    defaultValues: {
      name: {
        condition: "contains",
        value: "",
      },
      email: {
        condition: "contains",
        value: "",
      },
      roleId: {
        condition: "equals",
        value: "",
      },
      emailVerified: {
        condition: "equals",
        value: "",
      },
      isActivated: {
        condition: "equals",
        value: "",
      },
      createdAt: {
        condition: "equals",
        value: "",
      },
    },
  });

  const OnSubmit = (data: FormValue) => {
    try {
      const filter = ObjectConversionToFilterType(data);
      replaceFilterAdmin(filter);
      form.reset();
      close();
    } catch {
      form.reset();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader className="border-b border-admin-border pb-4">
          <DialogTitle className="text-xl text-admin-text-primary">
            Filtres avancés des administrateurs
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            ref={formRef}
            onSubmit={form.handleSubmit(OnSubmit)}
            className="flex flex-col gap-y-6"
          >
            {/* Nom + Email */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <SelectFormField
                  label="Nom"
                  form={form}
                  condition="String"
                  nameField="name.condition"
                />
                <Input {...form.register("name.value")} placeholder="Nom..." />
              </div>
              <div className="flex flex-col gap-2">
                <SelectFormField
                  label="Email"
                  form={form}
                  condition="String"
                  nameField="email.condition"
                />
                <Input {...form.register("email.value")} placeholder="Email..." />
              </div>
            </div>

            {/* Rôle + Inscrit le */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <SelectFormField
                  label="Rôle"
                  form={form}
                  condition="String"
                  nameField="roleId.condition"
                />
                <Select
                  value={form.watch("roleId.value")}
                  onValueChange={(value) => form.setValue("roleId.value", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Rôles</SelectLabel>
                      {roles?.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <SelectFormField
                  label="Inscrit le"
                  form={form}
                  condition="Date"
                  nameField="createdAt.condition"
                />
                <Input {...form.register("createdAt.value")} type="date" />
              </div>
            </div>

            {/* Activé + Email vérifié */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <SelectFormField
                  label="Activé"
                  form={form}
                  condition="Bool"
                  nameField="isActivated.condition"
                />
                <Select
                  value={form.watch("isActivated.value")}
                  onValueChange={(value) => form.setValue("isActivated.value", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Valeur</SelectLabel>
                      <SelectItem value="false">Non</SelectItem>
                      <SelectItem value="true">Oui</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <SelectFormField
                  label="Email vérifié"
                  form={form}
                  condition="Bool"
                  nameField="emailVerified.condition"
                />
                <Select
                  value={form.watch("emailVerified.value")}
                  onValueChange={(value) => form.setValue("emailVerified.value", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Valeur</SelectLabel>
                      <SelectItem value="false">Non</SelectItem>
                      <SelectItem value="true">Oui</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Boutons */}
            <DialogFooter className="border-t border-admin-border pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  form.reset();
                  resetFilterAdmin();
                  close();
                }}
              >
                Réinitialiser
              </Button>
              <Button
                type="submit"
                className="bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90"
              >
                {"Valider"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
